
import { useState, useMemo, useCallback } from "react";

interface UseTableSearchProps {
  data: any[];
  searchableFields: string[];
  enableInstantSearch?: boolean;
  onServerSearch?: (searchTerm: string) => void;
  debounceMs?: number;
}

export function useTableSearch({
  data,
  searchableFields,
  enableInstantSearch = true,
  onServerSearch,
  debounceMs = 300
}: UseTableSearchProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchTransition, setIsSearchTransition] = useState(false);
  
  // Client-side filtering
  const filteredData = useMemo(() => {
    if (!enableInstantSearch || !searchTerm.trim()) {
      return data;
    }
    
    const searchLower = searchTerm.toLowerCase();
    
    return data.filter(row => {
      return searchableFields.some(field => {
        const value = row[field];
        if (value == null) return false;
        
        // Handle arrays
        if (Array.isArray(value)) {
          return value.some(item => 
            String(item).toLowerCase().includes(searchLower)
          );
        }
        
        // Handle regular values
        return String(value).toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, searchableFields, enableInstantSearch]);
  
  // Debounced search handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    
    if (enableInstantSearch) {
      setIsSearchTransition(true);
      const timer = setTimeout(() => {
        setIsSearchTransition(false);
      }, 100);
      return () => clearTimeout(timer);
    } else if (onServerSearch) {
      setIsSearchTransition(true);
      const timer = setTimeout(() => {
        onServerSearch(value);
        setIsSearchTransition(false);
      }, debounceMs);
      return () => clearTimeout(timer);
    }
  }, [enableInstantSearch, onServerSearch, debounceMs]);
  
  const clearSearch = useCallback(() => {
    setSearchTerm("");
    if (!enableInstantSearch && onServerSearch) {
      onServerSearch("");
    }
  }, [enableInstantSearch, onServerSearch]);
  
  return {
    searchTerm,
    filteredData,
    isSearchTransition,
    handleSearchChange,
    clearSearch,
    isSearching: searchTerm.trim().length > 0,
    hasResults: filteredData.length > 0
  };
}
